import './Meter.css'

/**
 * A proportion, drawn as a track and a fill.
 *
 * The one mark this app uses for "part of a whole" outside a chart. It replaced
 * two things that were solving the same problem worse: four ECharts semi-gauges
 * on the KPI row, where a 7% arc spent most of a 92px tile on empty sweep and an
 * absent value drew a grey croissant with a dash in it; and a licence strip that
 * printed `125` and `375` as unrelated figures with nothing saying they were two
 * parts of the same 500.
 *
 * A bar reads at any size, compares across a row without the eye re-anchoring to
 * a new arc each time, and degrades honestly: no value is an empty track, which
 * looks like a thing waiting for data rather than a broken dial.
 *
 * `tone` is the fill's meaning. `value` is a percentage 0–100, or null.
 *
 * @param {{value: number|null, tone?: 'accent'|'success'|'warning'|'danger'|'neutral',
 *          size?: 'sm'|'md', label?: string}} props
 */
export default function Meter({ value, tone = 'accent', size = 'md', label, className = '' }) {
  const empty = value == null || Number.isNaN(value)
  const pct = empty ? 0 : Math.min(100, Math.max(0, value))

  return (
    <div
      className={`meter meter--${size} meter--${tone}${empty ? ' meter--empty' : ''} ${className}`.trim()}
      role="progressbar"
      aria-valuenow={empty ? undefined : Math.round(pct)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label}
    >
      {/* A hair of width on a non-zero fill, so "almost none" still reads as a
          mark rather than as nothing at all — 0.3% of a 300px track is 1px. A
          true zero gets none of it: the floor used to apply to 0 as well, and a
          nub on "nobody" is a measurement that was never taken. */}
      <span
        className="meter__fill"
        style={{ inlineSize: `${empty || pct === 0 ? 0 : Math.max(pct, 1.5)}%` }}
      />
    </div>
  )
}
